import { useMemo, useState } from 'react';
import {
  describeDayFilters,
  describeMonthFilters,
  describeWeekFilters,
  formatCalendarPageDate,
} from '@elva/ui';
import type {
  Appointment,
  CalendarDayFilterSection,
  CalendarFilterChip,
  CalendarMonthFilterSection,
  CalendarTimeDensity,
  CalendarViewMode,
  CalendarWeekFilterSection,
  FlagKey,
  KpiItem,
  MonthDayRecord,
  MonthWeekSummary,
  Provider,
  WeekDay,
  MonthGridCell,
  MonthPeekSelection,
} from '@elva/ui';
import { useLocationId } from '@/data/useLocationId';
import {
  adaptCalendarProvidersForGrid,
  adaptDayHeaderStats,
  adaptDayKpis,
  adaptMonthGrid,
  adaptNext7Days,
  adaptWeekAppointments,
  adaptWeekHeaderStats,
  adaptWeekKpis,
  adaptWeekTitleRange,
  extractLocalHour,
  normalizeFlagKey,
} from '../adapters/calendarAdapters';
import { dayFilterParams, monthFilterParams, weekFilterParams } from '../api/calendarFilterParams';
import { useCalendarDay } from './useCalendarDay';
import { useCalendarFilters } from './useCalendarFilters';
import { useCalendarMonth } from './useCalendarMonth';
import { useCalendarNext7 } from './useCalendarNext7';
import { useKpiToday } from './useKpiToday';
import type { CalendarCell } from '../api/types';

const VALID_FLAGS = new Set<FlagKey>([
  'elig_ok', 'forms_ok', 'confirmed', 'reminded', 'all_clear', 'forms_inc',
  'balance', 'preauth_p', 'anxiety', 'new_pt', 'big_case', 'unconfirmed',
  'elig_fail', 'lab_missing', 'med_alert', 'birthday', 'vip',
]);

type AppointmentStatus = Appointment['status'];

const VALID_STATUSES = new Set<AppointmentStatus>([
  'CONFIRMED', 'COMPLETED', 'CHECKED_IN', 'ARRIVED', 'TREATMENT',
  'SCHEDULED', 'PENDING', 'NO_SHOW', 'BROKEN', 'RESCHED',
]);

function cellColumnId(cell: CalendarCell, viewMode: CalendarViewMode): string {
  if (viewMode === 'Operatory') {
    return cell.operatory_column_id ?? cell.column_id;
  }
  return cell.provider_column_id ?? cell.column_id;
}

function cellToAppointment(cell: CalendarCell, viewMode: CalendarViewMode): Appointment {
  const baseFlags: FlagKey[] = (cell.flags ?? [])
    .map((f) => normalizeFlagKey(f) ?? (VALID_FLAGS.has(f as FlagKey) ? (f as FlagKey) : null))
    .filter((f): f is FlagKey => f != null);

  const readinessFlags: FlagKey[] = [];
  if (cell.readiness) {
    if (cell.readiness.elig) readinessFlags.push('elig_ok');
    else readinessFlags.push('elig_fail');
    if (cell.readiness.forms) readinessFlags.push('forms_ok');
    else readinessFlags.push('forms_inc');
    // confirmation_state (when sent) already decided confirmed/unconfirmed in the adapter.
    if (cell.readiness.confirmed && !cell.confirmation_state) readinessFlags.push('confirmed');
    if (cell.readiness.reminded) readinessFlags.push('reminded');
  }

  const allFlags = [...new Set([...baseFlags, ...readinessFlags])];

  const rawStatus = cell.status?.toUpperCase() ?? 'SCHEDULED';
  const status: AppointmentStatus = VALID_STATUSES.has(rawStatus as AppointmentStatus)
    ? (rawStatus as AppointmentStatus)
    : 'SCHEDULED';

  const durationHours =
    cell.duration_min != null ? Math.max(cell.duration_min / 60, 0.25) : 1;

  return {
    id: String(cell.appointment_id),
    providerId: cellColumnId(cell, viewMode),
    hour: extractLocalHour(cell.start) ?? 9,
    duration: durationHours,
    patient: cell.patient?.name ?? 'Unknown',
    status,
    production: Math.round((cell.production_cents ?? 0) / 100),
    procedure: cell.procedure?.name ?? '',
    flags: allFlags,
  };
}

function isoLocal(d = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDaysToIso(iso: string, delta: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + delta);
  return isoLocal(d);
}

function shiftMonth(year: number, month: number, delta: number) {
  const d = new Date(year, month - 1 + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function todayIso(): string {
  return isoLocal();
}

export function useCalendarState() {
  const filters = useCalendarFilters();
  const {
    view,
    setView,
    viewMode,
    setViewMode,
    dayFilters,
    weekFilters,
    monthFilters,
  } = filters;
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [selected, setSelected] = useState<Appointment | null>(null);
  const [monthPeek, setMonthPeek] = useState<MonthPeekSelection | null>(null);
  const [large, setLarge] = useState(false);
  const [timeDensity, setTimeDensity] = useState<CalendarTimeDensity>('Small');

  const locationId = useLocationId() ?? null;
  const now = new Date();
  const today = todayIso();
  const [dayDate, setDayDate] = useState(today);
  const [weekStart, setWeekStart] = useState(today);
  const [monthAnchor, setMonthAnchor] = useState(() => ({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  }));

  // Only the active view fetches — a disabled query makes no request (react-query v5:
  // a disabled query is isPending but not isFetching, so isLoading stays false).
  const isDayView = view === 'Day';
  const isWeekView = view === 'Week';
  const isMonthView = view === 'Month';

  // The server applies the Options filters; the params are part of each query key.
  const dayParams = useMemo(() => dayFilterParams(dayFilters), [dayFilters]);
  const weekParams = useMemo(() => weekFilterParams(weekFilters), [weekFilters]);
  const monthParams = useMemo(() => monthFilterParams(monthFilters), [monthFilters]);

  const { data: dayData, isLoading: isLoadingDayQuery, isError: isErrorDayQuery } =
    useCalendarDay(locationId, dayDate, { enabled: isDayView, filters: dayParams });
  const { data: weekData, isLoading: isLoadingWeek, isError: isErrorWeek } =
    useCalendarNext7(locationId, weekStart, { enabled: isWeekView, filters: weekParams });
  const { data: monthData, isLoading: isLoadingMonth, isError: isErrorMonth } =
    useCalendarMonth(locationId, monthAnchor.year, monthAnchor.month, {
      enabled: isMonthView,
      filters: monthParams,
    });

  const { data: kpiTodayData, isLoading: isLoadingKpiToday, isError: isErrorKpiToday } =
    useKpiToday(dayDate, { enabled: isDayView });

  const hasEmbeddedDayKpis = (dayData?.render?.kpis?.length ?? 0) > 0;

  const realAppointments = useMemo<Appointment[]>(() => {
    if (!dayData?.cells) return [];
    return dayData.cells
      .filter((c) => !c.rebuilding && cellColumnId(c, viewMode))
      .map((c) => cellToAppointment(c, viewMode));
  }, [dayData, viewMode]);

  // Full rosters (`meta.providers` / `meta.operatories` are unfiltered) — feed the Options drawer.
  const providerColumnsAll = useMemo<Provider[]>(() => {
    if (!dayData?.providers?.length) return [];
    const cells = (dayData.cells ?? []).map((c) => ({
      ...c,
      column_id: cellColumnId(c, 'Provider'),
    }));
    return adaptCalendarProvidersForGrid(dayData.providers, cells);
  }, [dayData]);

  const operatoryColumnsAll = useMemo<Provider[]>(() => {
    const operatories = dayData?.operatories ?? [];
    if (!operatories.length) return [];
    const cells = (dayData?.cells ?? []).map((c) => ({
      ...c,
      column_id: cellColumnId(c, 'Operatory'),
    }));
    return adaptCalendarProvidersForGrid(
      operatories.map((o) => ({ column_id: o.column_id, name: o.name, role: 'Operatory' })),
      cells,
    );
  }, [dayData]);

  const dayOperatorySelectOptions = useMemo(
    () => operatoryColumnsAll.map((o) => ({ id: o.id, name: o.name })),
    [operatoryColumnsAll],
  );

  // The server keeps every column; the ticked providers / operatories decide which ones show.
  const dayProviders = useMemo<Provider[]>(() => {
    const all = viewMode === 'Operatory' ? operatoryColumnsAll : providerColumnsAll;
    const ticked = viewMode === 'Operatory' ? dayFilters.operatories : dayFilters.providers;
    return ticked.size === 0 ? all : all.filter((p) => ticked.has(p.id));
  }, [viewMode, operatoryColumnsAll, providerColumnsAll, dayFilters.operatories, dayFilters.providers]);

  // The drawer shows un-ticked rows, so turn the ticked ids back into the ones left out.
  const hiddenProviderIds = useMemo<ReadonlySet<string>>(
    () =>
      dayFilters.providers.size === 0
        ? new Set()
        : new Set(providerColumnsAll.filter((p) => !dayFilters.providers.has(p.id)).map((p) => p.id)),
    [providerColumnsAll, dayFilters.providers],
  );
  const hiddenOperatoryIds = useMemo<ReadonlySet<string>>(
    () =>
      dayFilters.operatories.size === 0
        ? new Set()
        : new Set(
            operatoryColumnsAll.filter((o) => !dayFilters.operatories.has(o.id)).map((o) => o.id),
          ),
    [operatoryColumnsAll, dayFilters.operatories],
  );

  const toggleProvider = (id: string) => {
    filters.toggleProvider(id, providerColumnsAll.map((p) => p.id));
  };
  const toggleOperatory = (id: string) => {
    filters.toggleOperatory(id, operatoryColumnsAll.map((o) => o.id));
  };

  // The response already holds only the matching cells.
  const appointments = realAppointments;

  const weekDays = useMemo<WeekDay[]>(
    () => adaptNext7Days(weekData?.days ?? []),
    [weekData?.days],
  );

  const weekAppointments = useMemo(
    () => adaptWeekAppointments(weekData?.days ?? []),
    [weekData?.days],
  );

  const weekKpis = useMemo<KpiItem[]>(() => {
    if (weekData?.kpis?.length) return adaptDayKpis(weekData.kpis);
    return adaptWeekKpis(weekData?.week_kpis ?? []);
  }, [weekData?.kpis, weekData?.week_kpis]);

  const dayKpis = useMemo<KpiItem[]>(() => {
    const raw = dayData?.render?.kpis ?? kpiTodayData?.kpis ?? [];
    return adaptDayKpis(raw);
  }, [dayData?.render?.kpis, kpiTodayData?.kpis]);

  const dayHeaderStats = useMemo(
    () => adaptDayHeaderStats(dayData?.cells ?? [], dayData?.render?.kpis ?? kpiTodayData?.kpis),
    [dayData?.cells, dayData?.render?.kpis, kpiTodayData?.kpis],
  );

  const weekTitleRange = useMemo(
    () => adaptWeekTitleRange(weekData?.days ?? [], weekData?.meta),
    [weekData?.days, weekData?.meta],
  );

  const dayPageTitle = useMemo(
    () => formatCalendarPageDate(new Date(`${dayDate}T12:00:00`)),
    [dayDate],
  );

  const weekHeaderStats = useMemo(
    () => adaptWeekHeaderStats(weekData?.days ?? []),
    [weekData?.days],
  );

  const monthGrid = useMemo(() => {
    if (!monthData) {
      return {
        data: {} as Record<number, MonthDayRecord>,
        dataByDate: {} as Record<string, MonthDayRecord>,
        gridCells: [] as MonthGridCell[],
        today: { year: monthAnchor.year, month: monthAnchor.month - 1, day: now.getDate() },
        weekSummaries: [] as MonthWeekSummary[],
        headerStats: { projected: '—', mtd: '—', newPatients: '0', daysLeft: '0' },
        kpis: [] as KpiItem[],
        title: { month: '', year: String(monthAnchor.year) },
      };
    }
    const displayYear = monthData.meta?.year ?? monthAnchor.year;
    const displayMonth = monthData.meta?.month ?? monthAnchor.month;
    return adaptMonthGrid(monthData.days ?? [], monthData.summary, displayYear, displayMonth);
  }, [monthData, monthAnchor.year, monthAnchor.month, now]);

  // Week is LOCKED to the next 7 days (today → today+6) — no prev/next navigation.
  // Day steps by ±1 day (+ jump-to-date via goToDate); Month steps by ±1 month.
  const goPrev = () => {
    if (view === 'Day') setDayDate((s) => addDaysToIso(s, -1));
    else if (view === 'Month') setMonthAnchor(({ year, month }) => shiftMonth(year, month, -1));
    // Week: no-op (locked to next 7 days)
  };

  const goNext = () => {
    if (view === 'Day') setDayDate((s) => addDaysToIso(s, 1));
    else if (view === 'Month') setMonthAnchor(({ year, month }) => shiftMonth(year, month, 1));
    // Week: no-op (locked to next 7 days)
  };

  // Day-view jump-to-any-date (from the nav date picker).
  const goToDate = (iso: string) => {
    if (!iso) return;
    setDayDate(iso);
  };

  const goToday = () => {
    const t = todayIso();
    setDayDate(t);
    setWeekStart(t);
    setMonthAnchor({ year: now.getFullYear(), month: now.getMonth() + 1 });
  };

  const resetDayOptions = () => {
    filters.resetDayFilters(true);
    setLarge(false);
    setTimeDensity('Small');
  };

  const dayChips = useMemo(() => describeDayFilters(dayFilters), [dayFilters]);
  const weekChips = useMemo(() => describeWeekFilters(weekFilters), [weekFilters]);
  const monthChips = useMemo(() => describeMonthFilters(monthFilters), [monthFilters]);

  const filterChips: CalendarFilterChip[] =
    view === 'Month' ? monthChips : view === 'Week' ? weekChips : dayChips;

  const removeFilterChip = (id: string) => {
    if (view === 'Month') filters.clearMonthSection(id as CalendarMonthFilterSection);
    else if (view === 'Week') filters.clearWeekSection(id as CalendarWeekFilterSection);
    else filters.clearDaySection(id as CalendarDayFilterSection);
  };

  const clearAllFilters = () => {
    if (view === 'Month') filters.resetMonthFilters();
    else if (view === 'Week') filters.resetWeekFilters();
    else filters.resetDayFilters();
  };

  // "Showing X of Y" comes from the server's `summary`. Month cells are day summaries, not
  // appointments, so it has no such count.
  const summary = view === 'Week' ? weekData?.summary : view === 'Day' ? dayData?.summary : undefined;
  const filterCounts =
    view === 'Month'
      ? null
      : summary
        ? { matched: summary.matched_count, total: summary.total_count }
        : {
            matched: view === 'Week' ? weekAppointments.length : appointments.length,
            total: view === 'Week' ? weekAppointments.length : appointments.length,
          };

  return {
    view,
    setView,
    optionsOpen,
    setOptionsOpen,
    selected,
    setSelected,
    monthPeek,
    setMonthPeek,
    dayFilters,
    weekFilters,
    monthFilters,
    filters,
    filterChips,
    filterCounts,
    removeFilterChip,
    clearAllFilters,
    large,
    setLarge,
    viewMode,
    setViewMode,
    timeDensity,
    setTimeDensity,
    productionThreshold: dayFilters.minProduction,
    setProductionThreshold: filters.setMinProduction,
    resetDayOptions,
    appointments,
    dayProviders,
    dayProviderOptions: providerColumnsAll,
    dayOperatoryOptions: dayOperatorySelectOptions,
    hiddenProviderIds,
    hiddenOperatoryIds,
    toggleProvider,
    toggleOperatory,
    weekAppointments,
    weekDays,
    weekKpis,
    dayKpis,
    weekTitleRange,
    dayPageTitle,
    weekHeaderStats,
    dayHeaderStats,
    goPrev,
    goNext,
    goToday,
    goToDate,
    dayDate,
    monthGridData: monthGrid.data,
    monthGridDataByDate: monthGrid.dataByDate,
    monthGridCells: monthGrid.gridCells,
    monthToday: monthGrid.today,
    monthHeaderStats: monthGrid.headerStats,
    monthKpis: monthGrid.kpis,
    monthTitle: monthGrid.title,
    isLoadingDay:
      (isLoadingDayQuery || (isDayView && !hasEmbeddedDayKpis && isLoadingKpiToday)) &&
      locationId != null,
    isLoadingWeek: isLoadingWeek && locationId != null,
    isLoadingMonth: isLoadingMonth && locationId != null,
    isErrorDay:
      isErrorDayQuery ||
      (isDayView && !hasEmbeddedDayKpis && isErrorKpiToday),
    isErrorWeek: isErrorWeek && locationId != null,
    isErrorMonth: isErrorMonth && locationId != null,
  };
}
